import { useEffect, useRef, useState } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { MenuOrientation, ThemeDirection } from 'config';
import useConfig from 'hooks/useConfig';
import { useNavTheme } from 'contexts/NavThemeContext';

// third party
import { FormattedMessage } from 'react-intl';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// types
import { LinkTarget, NavItemType } from 'types';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

interface NavItemProps {
  item: NavItemType;
  level: number;
  isParents?: boolean;
  setSelectedID?: () => void;
}

export default function NavItem({ item, level, isParents = false, setSelectedID }: NavItemProps) {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const ref = useRef<HTMLSpanElement>(null);
  const { mode, tokens } = useNavTheme();
  const isLight = mode === 'light';

  const { pathname } = useLocation();
  const {
    state: { menuOrientation, themeDirection }
  } = useConfig();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;
  const isSelected = !!matchPath({ path: item?.link ? item.link : item.url!, end: false }, pathname);

  const [hoverStatus, setHover] = useState<boolean>(false);

  const compareSize = () => {
    const compare = ref.current && ref.current.scrollWidth > ref.current.clientWidth;
    setHover(compare as boolean);
  };

  useEffect(() => {
    compareSize();
    window.addEventListener('resize', compareSize);
    window.removeEventListener('resize', compareSize);
  }, []);

  const Icon = item?.icon!;
  const itemIcon = item?.icon ? (
    <Icon stroke={1.5} size={drawerOpen ? '20px' : '22px'} style={{ ...(isHorizontal && isParents && { fontSize: 20, stroke: '1.5' }) }} />
  ) : (
    <FiberManualRecordIcon sx={{ width: isSelected ? 8 : 6, height: isSelected ? 8 : 6 }} fontSize={level > 0 ? 'inherit' : 'medium'} />
  );

  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const itemHandler = () => {
    if (downMD) handlerDrawerOpen(false);

    if (isParents && setSelectedID) {
      setSelectedID();
    }
  };

  return (
    <>
      {!isHorizontal ? (
        <ListItemButton
          component={Link}
          to={item.url!}
          target={itemTarget}
          disabled={item.disabled}
          disableRipple
          sx={{
            zIndex: 1201,
            borderRadius: '10px',
            mb: 0.5,
            mx: drawerOpen ? 1.5 : 0.75,
            py: drawerOpen ? 1.25 : 0.5,
            px: drawerOpen ? 2 : 0.5,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.2s ease',
            justifyContent: drawerOpen ? 'flex-start' : 'center',
            // Base styling - clean design
            ...(drawerOpen ? {
              background: isSelected
                ? (isLight ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.12)')
                : 'transparent',
              border: isSelected
                ? `1px solid ${isLight ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.25)'}`
                : '1px solid transparent',
            } : {
              background: 'transparent',
              border: 'none',
            }),
            // Hover state
            '&:hover': drawerOpen ? {
              background: isSelected
                ? (isLight ? 'rgba(99, 102, 241, 0.12)' : 'rgba(99, 102, 241, 0.15)')
                : (isLight ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.03)'),
              border: isSelected
                ? `1px solid ${isLight ? 'rgba(99, 102, 241, 0.25)' : 'rgba(99, 102, 241, 0.3)'}`
                : `1px solid ${isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'}`,
            } : {
              background: 'transparent',
            },
            // Disabled state
            '&.Mui-disabled': {
              opacity: 0.4,
            },
          }}
          selected={isSelected}
          onClick={() => itemHandler()}
        >
          <ListItemIcon
            sx={{
              minWidth: drawerOpen ? 36 : 'auto',
              mr: drawerOpen ? 1.5 : 0,
              justifyContent: 'center',
              // Icon styling - clean design
              color: isSelected ? tokens.accentPrimary : tokens.textMuted,
              transition: 'all 0.2s ease',
              // Icon container for collapsed state
              ...(!drawerOpen && {
                width: 42,
                height: 42,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                background: isSelected
                  ? (isLight ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.12)')
                  : 'transparent',
                border: isSelected
                  ? `1px solid ${isLight ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.25)'}`
                  : '1px solid transparent',
                '&:hover': {
                  background: isLight ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.03)',
                  border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'}`,
                },
              }),
            }}
          >
            {itemIcon}
          </ListItemIcon>

          {drawerOpen && (
            <Tooltip title={<FormattedMessage id={item.title} />} disableHoverListener={!hoverStatus}>
              <ListItemText
                primary={
                  <Typography
                    ref={ref}
                    noWrap
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontWeight: isSelected ? 500 : 400,
                      fontSize: '0.875rem',
                      letterSpacing: '0.01em',
                      color: isSelected ? tokens.textPrimary : tokens.textMuted,
                      transition: 'color 0.3s ease',
                      ...(themeDirection === ThemeDirection.RTL && { textAlign: 'end', direction: 'rtl' }),
                    }}
                  >
                    <FormattedMessage id={item.title} />
                  </Typography>
                }
                secondary={
                  item.caption && (
                    <Typography
                      variant="caption"
                      gutterBottom
                      sx={{
                        display: 'block',
                        fontSize: '0.7rem',
                        fontWeight: 400,
                        color: tokens.textDimmed,
                        textTransform: 'capitalize',
                        lineHeight: 1.5,
                        mt: 0.25,
                      }}
                    >
                      <FormattedMessage id={item.caption} />
                    </Typography>
                  )
                }
              />
            </Tooltip>
          )}

          {drawerOpen && item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                background: tokens.bgCard,
                border: `1px solid ${tokens.borderMedium}`,
                color: tokens.accentTertiary,
                '& .MuiChip-label': {
                  px: 0.75,
                },
              }}
            />
          )}
        </ListItemButton>
      ) : (
        <ListItemButton
          component={Link}
          to={item.url!}
          target={itemTarget}
          disabled={item.disabled}
          sx={{
            borderRadius: '8px',
            mb: isParents ? 0 : 0.5,
            alignItems: 'flex-start',
            backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
            py: 1,
            pl: 2,
            mr: isParents ? 1 : 0,
            transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            '&:hover': {
              background: tokens.bgCardHover,
            },
            ...(isSelected && {
              background: tokens.bgSelected,
              border: `1px solid ${tokens.borderSubtle}`,
            }),
          }}
          selected={isSelected}
          onClick={() => itemHandler()}
        >
          <ListItemIcon
            sx={{
              my: 'auto',
              minWidth: !item?.icon ? 18 : 36,
              color: isSelected ? tokens.accentTertiary : tokens.iconDefault,
            }}
          >
            {itemIcon}
          </ListItemIcon>

          <ListItemText
            sx={{ mb: 0.25 }}
            primary={
              <Typography
                sx={{
                  fontWeight: isSelected ? 500 : 400,
                  color: isSelected ? tokens.textPrimary : tokens.textMuted,
                }}
              >
                <FormattedMessage id={item.title} />
              </Typography>
            }
            secondary={
              item.caption && (
                <Typography
                  gutterBottom
                  sx={{
                    display: 'block',
                    fontSize: '0.6875rem',
                    fontWeight: 400,
                    color: tokens.textDimmed,
                    textTransform: 'capitalize',
                    lineHeight: 1.66,
                  }}
                >
                  {item.caption}
                </Typography>
              )
            }
          />

          {item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
              sx={{
                height: 20,
                fontSize: '0.65rem',
                background: tokens.bgCard,
                border: `1px solid ${tokens.borderMedium}`,
                color: tokens.accentTertiary,
              }}
            />
          )}
        </ListItemButton>
      )}
    </>
  );
}
